import { useState, useEffect, useCallback, useRef } from 'react';
import { useApi, useAuth } from './useAuth';
import { useSSE } from './useSSE';

// ─── Types ───────────────────────────────────────────────────────

export interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  senderAvatar: string | null;
  text: string;
  timestamp: Date;
  type: 'text' | 'system' | 'spark_reveal';
}

export interface ChatRoomInfo {
  id: string;
  matchId: string;
  participants: string[];
  participantNames: Record<string, string>;
  chatType: 'singles' | 'couples';
  unlocksAt: Date;
  isUnlocked: boolean;
  sparkAnswersRevealed: boolean;
  minutesUntilUnlock: number;
  hoursUntilUnlock: number;
}

// ─── Hook ────────────────────────────────────────────────────────
// Polls the API for new messages since DynamoDB doesn't have
// real-time listeners like DynamoDB. In production, replace
// with API Gateway WebSocket for true real-time.

export function useChat(chatRoomId: string | null) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [roomInfo, setRoomInfo] = useState<ChatRoomInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { apiFetch } = useApi();
  const { userProfile } = useAuth();
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchRoomInfo = useCallback(async () => {
    if (!chatRoomId) return;
    try {
      const data = await apiFetch(`/api/chat/rooms/${chatRoomId}`);
      setRoomInfo(data.room);
    } catch (err: any) {
      setError(err.message);
    }
  }, [chatRoomId, apiFetch]);

  const fetchMessages = useCallback(async () => {
    if (!chatRoomId) return;
    try {
      const data = await apiFetch(`/api/chat/rooms/${chatRoomId}/messages?limit=100`);
      const msgs = (data.messages || []).map((m: any) => ({
        ...m,
        timestamp: new Date(m.timestamp),
      }));
      setMessages(msgs);
    } catch (err: any) {
      // Don't overwrite existing error if we already have messages
      if (messages.length === 0) setError(err.message);
    }
  }, [chatRoomId, apiFetch, messages.length]);

  // Initial load
  useEffect(() => {
    if (!chatRoomId) { setLoading(false); return; }

    setLoading(true);
    Promise.all([fetchRoomInfo(), fetchMessages()])
      .finally(() => setLoading(false));
  }, [chatRoomId]);

  // SSE-driven message fetch: refetch whenever a chat_message event arrives for this room
  useSSE(useCallback((data: { chatRoomId: string }) => {
    if (data.chatRoomId === chatRoomId) {
      fetchMessages();
    }
  }, [chatRoomId, fetchMessages]));

  // Fallback poll in case SSE is unavailable (e.g. App Runner cold start).
  // SSE already delivers messages in real time, so this only needs to be a
  // safety net — it used to run every 10s, which (multiplied across open chats
  // and accounts) burned through the API rate limit on its own.
  useEffect(() => {
    if (!chatRoomId || !roomInfo?.isUnlocked) return;

    pollRef.current = setInterval(() => {
      fetchMessages();
    }, 30_000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [chatRoomId, roomInfo?.isUnlocked, fetchMessages]);

  // Poll for unlock status every minute when chat is locked
  useEffect(() => {
    if (!chatRoomId || roomInfo?.isUnlocked) return;

    const interval = setInterval(fetchRoomInfo, 60 * 1000);
    return () => clearInterval(interval);
  }, [chatRoomId, roomInfo?.isUnlocked, fetchRoomInfo]);

  const sendMessage = useCallback(async (text: string) => {
    if (!chatRoomId) throw new Error('No chat room');

    // Optimistically append the message immediately.
    // Stamp it with the real user id so isMe (msg.senderId === myId) is true
    // right away and the bubble renders on the correct (right) side — otherwise
    // it flashes left-aligned until the server round-trip returns the real message.
    const tempId = `temp-${Date.now()}`;
    const optimistic: ChatMessage = {
      id: tempId,
      senderId: userProfile?.id ?? 'me',
      senderName: 'You',
      senderAvatar: null,
      text,
      timestamp: new Date(),
      type: 'text',
    };
    setMessages(prev => [...prev, optimistic]);

    try {
      await apiFetch(`/api/chat/rooms/${chatRoomId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ text }),
      });
      // Replace optimistic message with real one from server
      await fetchMessages();
    } catch (err) {
      // Roll back on failure
      setMessages(prev => prev.filter(m => m.id !== tempId));
      throw err;
    }
  }, [chatRoomId, apiFetch, fetchMessages, userProfile?.id]);

  const checkUnlock = useCallback(async () => {
    if (!chatRoomId) return;
    await apiFetch(`/api/chat/rooms/${chatRoomId}/unlock-check`, { method: 'POST' });
    await fetchRoomInfo();
  }, [chatRoomId, apiFetch, fetchRoomInfo]);

  return {
    messages, roomInfo, loading, error,
    sendMessage, checkUnlock, refreshRoomInfo: fetchRoomInfo,
  };
}
